import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { NavigationService } from '../../../core/services/navigation.service';
import { PermissionService } from '../../../core/auth/permission.service';
import { TranslateModule } from '@ngx-translate/core';
import { MESSAGE_KEYS } from '../../constants/messages';

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navigationService = inject(NavigationService);
  private readonly permissionService = inject(PermissionService);

  readonly backLabelKey = MESSAGE_KEYS.COMMON.BACK;

  readonly titleKey = signal<string | null>(null);
  readonly subtitleKey = signal<string | null>(null);
  readonly previousUrl = signal<string | null>(null);

  ngOnInit(): void {
    this.buildFromRoute();
    // update on navigation
    this.router.events.subscribe(() => this.buildFromRoute());
  }
  private buildFromRoute(): void {
    const crumbs = this.collectCrumbs();

    // current/page title is last crumb by default
      const last = crumbs.at(-1);
    this.titleKey.set(last?.labelKey ?? null);

    // subtitle and title override via route data (pageTitleKey / pageSubtitleKey)
    const deepest = this.getDeepestRouteSnapshot();
    const pageTitleKey = deepest?.data?.['pageTitleKey'] as string | undefined;
    const pageSubtitleKey = deepest?.data?.['pageSubtitleKey'] as string | undefined;

    if (pageTitleKey) this.titleKey.set(pageTitleKey);

    this.subtitleKey.set(pageSubtitleKey ?? null);

    // previous URL is the second to last crumb (if exists)
      this.previousUrl.set(crumbs.at(-2)?.url ?? null);
  }

  private collectCrumbs(): { labelKey: string; url: string }[] {
    // Order and Requester profiles have no Home page — omit the Home crumb so the
    // back button is not shown on their initial/landing page.
    const showHome = !this.permissionService.isOrder() && !this.permissionService.isRequester();
    const crumbs: { labelKey: string; url: string }[] = showHome
      ? [{ labelKey: 'private.breadcrumb.home', url: '/app' }]
      : [];

    let route = this.activatedRoute.root;
    let currentUrl = '';

    while (route.firstChild) {
      route = route.firstChild;
      const snapshot = route.snapshot;
      if (!snapshot.url || snapshot.url.length === 0) continue;

      snapshot.url.forEach(segment => (currentUrl += `/${segment.path}`));
      this.appendAncestorCrumbs(crumbs, snapshot.data, currentUrl);

      const breadcrumbKey = snapshot.data['breadcrumb'] as string | undefined;
      // For restricted profiles (order/requester), skip any Home crumb that
      // appears on parent/intermediate route segments (e.g. the 'shipper' parent
      // route in app.routes.ts carries breadcrumb='private.breadcrumb.home').
      // Without this, even with the initial crumbs array empty, the loop would
      // still push a Home entry → producing 2 crumbs → showing the back button.
      const isHomeCrumb = breadcrumbKey === 'private.breadcrumb.home';
      if (breadcrumbKey && (showHome || !isHomeCrumb)) {
        crumbs.push({ labelKey: breadcrumbKey, url: currentUrl });
      }
    }

    return crumbs;
  }

  private appendAncestorCrumbs(
    crumbs: { labelKey: string; url: string }[],
    data: Record<string, unknown>,
    currentUrl: string,
  ): void {
    const grandparentBreadcrumb = data['grandparentBreadcrumb'] as string | undefined;
    const grandparentUrl = data['grandparentUrl'] as string | undefined;
    const parentBreadcrumb = data['parentBreadcrumb'] as string | undefined;
    const parentUrl = data['parentUrl'] as string | undefined;
    const breadcrumbKey = data['breadcrumb'] as string | undefined;

    this.pushUniqueCrumb(crumbs, grandparentBreadcrumb, grandparentUrl ?? currentUrl);
    if (parentBreadcrumb) {
      this.pushUniqueCrumb(crumbs, parentBreadcrumb, parentUrl ?? currentUrl);
    } else {
      this.pushCompaniesBackcompatCrumb(crumbs, breadcrumbKey);
    }
  }

  private pushUniqueCrumb(
    crumbs: { labelKey: string; url: string }[],
    labelKey: string | undefined,
    url: string,
  ): void {
    if (!labelKey) return;
    const lastCrumb = crumbs.at(-1);
    if (!lastCrumb || lastCrumb.labelKey !== labelKey) {
      crumbs.push({ labelKey, url });
    }
  }

  private pushCompaniesBackcompatCrumb(
    crumbs: { labelKey: string; url: string }[],
    breadcrumbKey: string | undefined,
  ): void {
    const isCompanyForm =
      breadcrumbKey === 'private.breadcrumb.edit_company' ||
      breadcrumbKey === 'private.breadcrumb.new_company';
    if (!isCompanyForm) return;
    // Backwards-compatibility: insert companies crumb when parentBreadcrumb missing
    const companiesCrumbKey = 'private.breadcrumb.companies';
    const companiesUrl = this.deriveSettingsBaseRoute() + '/companies';
    this.pushUniqueCrumb(crumbs, companiesCrumbKey, companiesUrl);
  }

  private getDeepestRouteSnapshot() {
    let route = this.activatedRoute.root;
    let snapshot = route.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
      snapshot = route.snapshot;
    }
    return snapshot;
  }

  // Try to infer whether we are under configurations-carrier or configurations-shipper
  // and return the base settings route accordingly. This mirrors logic used by
  // PrivateToolbar to derive the companies listing path when routes don't provide
  // an explicit parentUrl.
  private deriveSettingsBaseRoute(): string {
    // Attempt to detect the settings base from current url
    const full = this.router.url || '';
    if (full.includes('/configurations-carrier')) return '/app/configurations-carrier';
    if (full.includes('/configurations-shipper')) return '/app/configurations-shipper';
    // fallback to carrier settings as sensible default
    return '/app/configurations-carrier';
  }

  goBack(): void {
    // Prefer actual navigation history (reflects the sequence the user visited)
    const navPrev = this.navigationService.getPreviousUrl();
    if (navPrev) {
      this.router.navigateByUrl(navPrev);
      return;
    }

    // If no history entry available, fall back to breadcrumb-derived URL
    const prev = this.previousUrl();
    if (prev) {
      this.router.navigateByUrl(prev);
      return;
    }

    // Last resort: browser history
    history.back();
  }
}
